import { memo, MouseEvent, useRef } from 'react';
import {
  Card,
  CardContent,
  Box,
  IconButton,
  Link as MuiLink,
  Grid,
  Tooltip,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { canReportGeoEventType, useAuthState } from 'state/useAuthState';
import { Report, ReportMedia } from 'features/reports/report.types';
import { getReportDetailPath } from 'features/reports/reports.utils';
import { RichTextContent } from 'components/RichTextContent';
import { ReportMetadata } from 'features/reports/components/ReportMetadata';
import { ReportableGeoEventType } from 'features/geoEvents/geoEvent.types';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import { isSupportedEmbedUrl } from 'shared/embedHelpers';
import { maybeTransformEmptyRichTextContentToNull } from 'shared/utils';
import { MediaCardEmbed } from './MediaCardEmbed';

type ReportCardProps = {
  media: ReportMedia | null;
  report: Report;
  className?: string;
  geoEventType: ReportableGeoEventType;
  geoEventIsActive: boolean;
  isEditReportDisabled: boolean;
  onEditClick: (event: MouseEvent<HTMLButtonElement>, report: Report) => void;
};

const useStyles = makeStyles()((theme) => ({
  root: {
    borderRadius: 0,
  },
  message: {
    whiteSpace: 'pre-line',
    wordBreak: 'break-word',
    // `* + *` spaces blocks 8px apart with no leading/trailing space, so the
    // Grid gap owns the outer edges. Padding, not margin: Virtuoso's
    // contentRect measurement excludes margins, so any protruding out of an
    // item corrupt the list's scroll height. See https://virtuoso.dev/react-virtuoso/#caveats
    '& > *': {
      margin: 0,
    },
    '& > * + *': {
      paddingTop: theme.spacing(1),
    },
  },
  attribution: {
    '&& p': {
      fontSize: theme.typography.subtitle1.fontSize,
      color: theme.palette.text.primary,
      margin: 0,
    },
  },
  mediaImage: {
    // Avoid the baseline gap under inline images.
    display: 'block',
    width: '100%',
    height: 'auto',
    objectFit: 'contain',
  },
}));

const ReportCardComponent = (props: ReportCardProps): JSX.Element => {
  const {
    report,
    media,
    className,
    geoEventType,
    geoEventIsActive,
    isEditReportDisabled,
    onEditClick,
  } = props;
  const { id, geoEventId, message } = report;
  const { classes, cx } = useStyles();
  const { permissions } = useAuthState();
  const { t } = useTranslation();
  const triedImgFallback = useRef(false);
  const location = useLocation();

  const canReport = canReportGeoEventType(permissions, geoEventType);

  return (
    <Card
      className={cx(classes.root, className)}
      data-testid="reportCard"
      elevation={0}
    >
      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
        <Grid container spacing={1}>
          <Grid size={12} container>
            <Grid size={canReport ? 11 : 12}>
              <ReportMetadata
                report={report}
                displayRelativeDate={geoEventIsActive}
              />
            </Grid>

            {canReport && (
              <Grid size={1}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Tooltip
                    title={
                      isEditReportDisabled
                        ? t('geoEvent.reports.editReportDisabledTooltip')
                        : undefined
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        aria-label={t('geoEvent.reports.editReport')}
                        onClick={(event) => onEditClick(event, report)}
                        disabled={isEditReportDisabled}
                      >
                        <MoreHoriz />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Grid>
            )}
          </Grid>

          {media && (
            <Grid size={12}>
              <MuiLink
                to={{
                  pathname: getReportDetailPath({
                    geoEventType,
                    geoEventId,
                    reportId: id,
                  }),
                  state: { fromRoute: location.pathname + location.search },
                }}
                underline="none"
                component={Link}
                sx={{ display: 'block' }}
              >
                <img
                  src={media.thumbnailUrl || media.url}
                  alt={t('geoEvent.reports.image')}
                  className={classes.mediaImage}
                  onError={(
                    event: React.SyntheticEvent<HTMLImageElement, Event>,
                  ) => {
                    // provide backup for the case of a thumbnailUrl failing, media.url should always work
                    const target = event.currentTarget as HTMLImageElement;
                    if (target.src !== media.url && !triedImgFallback.current) {
                      target.src = media.url;
                      triedImgFallback.current = true;
                    }
                    target.onerror = null;
                  }}
                  loading="lazy"
                />
              </MuiLink>
            </Grid>
          )}

          {media?.attribution && (
            <Grid size={12}>
              <RichTextContent
                content={media.attribution}
                className={classes.attribution}
              />
            </Grid>
          )}

          {report.embedUrl && isSupportedEmbedUrl(report.embedUrl) && (
            <Grid size={12}>
              <MediaCardEmbed embedUrl={report.embedUrl} />
            </Grid>
          )}

          {maybeTransformEmptyRichTextContentToNull(message) && (
            <Grid size={12}>
              <RichTextContent content={message} className={classes.message} />
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export const ReportCard = memo(ReportCardComponent);
